/**
 * Authentication Context
 *
 * Manages authentication state using Authentik OAuth2/OIDC.
 * Handles token storage, verification, and refresh.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// Token storage keys
const ACCESS_TOKEN_KEY = 'mhf_access_token';
const REFRESH_TOKEN_KEY = 'mhf_refresh_token';
const USER_KEY = 'mhf_user';

/** API base for auth (same as api.js: includes base path when under /us/cars/, etc.) */
function getAuthBase() {
  if (typeof window === 'undefined') return '/api';
  const base = (window.__BASE_PATH__ || '').replace(/\/$/, '');
  return base ? `${base}/api` : '/api';
}

/**
 * Parse JWT token without verification (for client-side use)
 */
function parseJwt(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token) {
  const decoded = parseJwt(token);
  if (!decoded || !decoded.exp) {
    return true;
  }
  // Add 60 second buffer
  return decoded.exp * 1000 < Date.now() + 60000;
}

/**
 * Extract user info from token
 */
function getUserFromToken(token) {
  const decoded = parseJwt(token);
  if (!decoded) {
    return null;
  }

  return {
    id: decoded.sub,
    email: decoded.email,
    name: decoded.name || decoded.given_name || decoded.email,
    username: decoded.preferred_username || decoded.email?.split('@')[0] || 'user',
    emailVerified: decoded.email_verified || false
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Clear all auth data
   */
  const clearAuth = useCallback(() => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setUser(null);
    setAccessToken(null);
  }, []);

  /**
   * Save auth data
   */
  const saveAuth = useCallback((tokens) => {
    const { access_token, refresh_token } = tokens;

    if (access_token) {
      localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
      setAccessToken(access_token);

      const userData = getUserFromToken(access_token);
      if (userData) {
        localStorage.setItem(USER_KEY, JSON.stringify(userData));
        setUser(userData);
      }
    }

    if (refresh_token) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
    }
  }, []);

  /**
   * Refresh access token
   */
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) {
      return false;
    }

    try {
      const response = await fetch(`${getAuthBase()}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const tokens = await response.json();
      saveAuth(tokens);
      return true;
    } catch (err) {
      console.error('Token refresh error:', err);
      clearAuth();
      return false;
    }
  }, [saveAuth, clearAuth]);

  /**
   * Initialize auth state from storage
   */
  useEffect(() => {
    async function initAuth() {
      setIsLoading(true);

      // Check for tokens in URL (OAuth callback)
      const params = new URLSearchParams(window.location.search);
      const urlAccessToken = params.get('access_token');
      const urlRefreshToken = params.get('refresh_token');
      const authError = params.get('auth_error');

      if (authError) {
        setError(authError);
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      if (urlAccessToken) {
        // Save tokens from URL
        saveAuth({
          access_token: urlAccessToken,
          refresh_token: urlRefreshToken
        });
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      // Check stored tokens
      const storedToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedUser = localStorage.getItem(USER_KEY);

      if (storedToken) {
        if (isTokenExpired(storedToken)) {
          // Try to refresh
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            setIsLoading(false);
            return;
          }
        } else {
          setAccessToken(storedToken);
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch {
              // Invalid stored user, extract from token
              setUser(getUserFromToken(storedToken));
            }
          }
        }
      }

      setIsLoading(false);
    }

    initAuth();
  }, [saveAuth, refreshAccessToken]);

  /**
   * Login - redirect to Authentik
   * returnTo must be a string (current path). Ignore event objects from onClick.
   */
  const login = useCallback((returnTo = window.location.pathname) => {
    const path = typeof returnTo === 'string' ? returnTo : window.location.pathname;
    const loginUrl = `${getAuthBase()}/auth/login?returnTo=${encodeURIComponent(path)}`;
    window.location.href = loginUrl;
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(async () => {
    try {
      await fetch(`${getAuthBase()}/auth/logout`, { method: 'POST' });
    } catch (err) {
      console.error('Logout error:', err);
    }
    clearAuth();
  }, [clearAuth]);

  /**
   * Get current access token (with auto-refresh)
   */
  const getAccessToken = useCallback(async () => {
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);

    if (!token) {
      return null;
    }

    if (isTokenExpired(token)) {
      const refreshed = await refreshAccessToken();
      if (!refreshed) {
        return null;
      }
      return localStorage.getItem(ACCESS_TOKEN_KEY);
    }

    return token;
  }, [refreshAccessToken]);

  /**
   * Make authenticated API request
   */
  const authFetch = useCallback(async (url, options = {}) => {
    const token = await getAccessToken();

    const headers = {
      ...options.headers
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(url, {
      ...options,
      headers
    });
  }, [getAccessToken]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login,
    logout,
    getAccessToken,
    authFetch,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
